import { ISnowStatus, OpenFlag } from '@/api/resort/types';

// Snow-report tables show base depth as a range spanning the mountain —
// the depth measured at the base up to the depth at the summit
// (e.g. "20-55\"") — rather than the base figure alone. Mirrors the
// legacy BaseDepthCell behaviour so the refactored tables stay at parity
// with production.

/**
 * Whether the resort has any depth worth rendering. Resorts flagged
 * NoReportAvailable are excluded even when the payload still carries
 * stale numbers.
 */
export const hasBaseDepth = (
  snow?: Partial<ISnowStatus>,
  openFlag?: OpenFlag,
): boolean => (
  openFlag !== OpenFlag.NoReportAvailable
  && (!!snow?.base || !!snow?.summit || !!snow?.middle)
);

/**
 * The values to feed the distance formatter, low end first.
 *
 * Some resorts only report a mid-mountain depth, so when neither base nor
 * summit is populated we fall back to `middle` as a single value. The
 * formatter collapses `[n, n]` to one reading via its `removeEqual`
 * option, so equal base/summit depths render as a single figure.
 */
export const baseDepthValues = (
  snow?: Partial<ISnowStatus>,
): Array<number | undefined> => (
  !snow?.base && !snow?.summit
    ? [snow?.middle]
    : [snow?.base, snow?.summit]
);

/**
 * The number the base-depth column sorts on: the low end of the range the
 * cell actually renders, or `undefined` for cells that render a dash so
 * they group at the bottom in either direction.
 */
export const baseDepthSortValue = (
  snow?: Partial<ISnowStatus>,
  openFlag?: OpenFlag,
): number | undefined => (
  hasBaseDepth(snow, openFlag)
    ? baseDepthValues(snow).find((v) => v !== undefined)
    : undefined
);
